import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 2rem;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 1rem;
    padding-top: 5rem;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -50px;
    left: -50px;
    width: 200px;
    height: 200px;
    background: #ff6b00;
    opacity: 0.1;
    transform: rotate(45deg);
  }
`;

const Content = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  display: grid;
  grid-template-columns: 0.8fr 1.2fr;
  gap: 4rem;
  align-items: center;
  z-index: 1;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const TextContent = styled.div`
  color: white;
  grid-row: 1;

  @media (max-width: 768px) {
    grid-row: 2;
    text-align: center;
  }
`;

const MediaContent = styled.div`
  grid-row: 1;

  @media (max-width: 768px) {
    grid-row: 1;
  }
`;

const Introducing = styled.h3`
  color: #666;
  text-transform: uppercase;
  letter-spacing: 8px;
  font-size: 1rem;
  margin-bottom: 1rem;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    letter-spacing: 6px;
  }
`;

const BrandName = styled.h2`
  color: #ff6b00;
  font-size: 2.5rem;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Title = styled.h1`
  font-size: 4rem;
  margin-bottom: 2rem;
  line-height: 1.1;
  color: white;

  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
  }
`;

const Description = styled.div`
  color: #999;
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  
  @media (max-width: 768px) {
    justify-content: center;
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;
  }
`;

const DetailsButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #ff6b00;
  color: white;
  padding: 0.75rem 2rem;
  text-decoration: none;
  border-radius: 4px;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: #ff8533;
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 0.875rem 1.5rem;
  }
`;

const ContactButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  color: white;
  padding: 0.75rem 2rem;
  text-decoration: none;
  border-radius: 4px;
  font-size: 1rem;
  transition: all 0.3s ease;
  border: 1px solid #ff6b00;
  
  &:hover {
    background: rgba(255, 107, 0, 0.1);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    margin: 0;
    padding: 0.75rem;
  }
`;

const ImageContainer = styled(motion.div)`
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -20%;
    right: -20%;
    width: 80%;
    height: 140%;
    background: linear-gradient(45deg, rgba(255,107,0,0.1), transparent);
    z-index: -1;
  }
`;

const ProductMedia = styled(motion.div)`
  width: 120%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: -20%;

  @media (max-width: 768px) {
    width: 100%;
    margin-right: 0;
    max-height: 40vh;
  }
`;

const ProductImage = styled(motion.img)`
  width: 100%;
  height: auto;
  filter: drop-shadow(0 10px 20px rgba(0,0,0,0.3));
  max-height: 70vh;
  object-fit: contain;

  @media (max-width: 768px) {
    max-height: 40vh;
    width: auto;
    margin: 0 auto;
  }
`;

const ProductVideo = styled(motion.video)`
  width: 100%;
  height: auto;
  filter: drop-shadow(0 10px 20px rgba(0,0,0,0.3));
  border-radius: 4px;
  max-height: 70vh;
  object-fit: contain;

  @media (max-width: 768px) {
    max-height: 40vh;
    width: auto;
    margin: 0 auto;
  }
`;

const NavigationButtons = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 2rem;
  display: flex;
  gap: 1rem;
  align-items: center;

  @media (max-width: 768px) {
    bottom: 1rem;
    left: 50%;
    transform: translateX(-50%);
  }
`;

const ProgressBar = styled(motion.div)`
  position: absolute;
  bottom: 4rem;
  left: 50%;
  transform: translateX(-50%);
  width: 200px;
  height: 2px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 1px;
  overflow: hidden;

  @media (max-width: 768px) {
    bottom: 3rem;
    width: 150px;
  }
`;

const ProgressIndicator = styled(motion.div)`
  height: 100%;
  background: #ff6b00;
  width: 100%;
`;

const NavButton = styled.button`
  width: 40px;
  height: 40px;
  border: 1px solid rgba(255,255,255,0.2);
  background: rgba(0,0,0,0.3);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  outline: none;
  
  &:hover {
    background: #ff6b00;
    border-color: #ff6b00;
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:focus {
    border-color: #ff6b00;
  }
`;

interface Product {
  id: number;
  name: string;
  title: string;
  description: string[];
  image: string;
  type?: 'image' | 'video';
}

const products: Product[] = [
  {
    id: 1,
    name: 'RSR',
    title: 'DirectTorque X20',
    description: [
      'A powerful 20 Nm direct drive wheelbase.',
      'Built and engineered with the spirit of DIY.',
      'Experience precision and immersion like never before.'
    ],
    image: '/assets/rsr-dt-x20-grass-2.png',
    type: 'image'
  },
  {
    id: 2,
    name: 'RSR',
    title: 'MotionPro',
    description: [
      'Advanced 3 degree of freedom motion',
      '4 linear strong actuators',
      'With 150mm of travel'
    ],
    image: '/assets/rsr-motionpro.mp4',
    type: 'video'
  }
];

const SLIDE_DURATION = 10; // Duration in seconds

const Hero = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const currentProduct = products[currentIndex];

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % products.length);
  }, []);

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + products.length) % products.length);
  };

  const pauseAutoPlay = () => {
    setIsAutoPlaying(false);
  };

  const resumeAutoPlay = () => {
    setIsAutoPlaying(true);
  };

  useEffect(() => {
    if (!isAutoPlaying) return;

    const timer = setInterval(nextSlide, SLIDE_DURATION * 1000);
    return () => clearInterval(timer);
  }, [isAutoPlaying, nextSlide]);

  return (
    <HeroSection>
      <NavigationButtons
      style={{zIndex: 1000}}
        onMouseEnter={pauseAutoPlay}
        onMouseLeave={resumeAutoPlay}
      >
        <NavButton onClick={prevSlide}>←</NavButton>
        <NavButton onClick={nextSlide}>→</NavButton>
      </NavigationButtons>
      <AnimatePresence mode='wait'>
        <Content key={currentProduct.id}>
          <TextContent>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
            >
              <Introducing>
                {currentProduct.title === 'DirectTorque X20' && (
                  'INTRODUCING:'
                )}
                {currentProduct.title === 'MotionPro' && (
                  'COMING SOON:'
                )}
              </Introducing>
              <BrandName>{currentProduct.name}</BrandName>
              <Title>{currentProduct.title}</Title>
              <Description>
                {currentProduct.description.map((line, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {line}<br />
                  </motion.div>
                ))}
              </Description>
              <ButtonContainer>
                <DetailsButton to={currentProduct.title === 'DirectTorque X20' ? '/directtorque' : '/motionpro'}>
                  Details
                  <span>→</span>
                </DetailsButton>
              </ButtonContainer>
              <ButtonContainer style={{paddingTop:15}}>
                {currentProduct.title === 'DirectTorque X20' && (
                  <ContactButton to="https://wa.me/6281916010121">
                    Contact Us for free trial
                    <span>→</span>
                  </ContactButton>
                )}
              </ButtonContainer>
              {/* MotionPro buttons 
              <ButtonContainer style={{paddingTop:15}}>
                {currentProduct.title === 'MotionPro' && (
                  <ContactButton to="/contact-us">
                    Contact us for customization & better deal
                    <span>→</span>
                  </ContactButton>
                )}
              </ButtonContainer>
              <ButtonContainer style={{paddingTop:15}}>
                {currentProduct.title === 'MotionPro' && (
                  <ContactButton to="/contact-us">
                    Check product bundling with GT-Sim ID
                    <span>→</span>
                  </ContactButton>
                )}
              </ButtonContainer>
              */}
            </motion.div>
          </TextContent>
          <MediaContent>
            <ImageContainer
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.5 }}
            >
              <ProductMedia>
                {currentProduct.type === 'video' ? (
                  <ProductVideo
                    autoPlay
                    loop
                    muted
                    playsInline
                    key={currentProduct.image}
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                  >
                    <source src={currentProduct.image} type="video/mp4" />
                    Your browser does not support the video tag.
                  </ProductVideo>
                ) : (
                  <ProductImage 
                    src={currentProduct.image} 
                    alt={`${currentProduct.name} ${currentProduct.title}`}
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                  />
                )}
              </ProductMedia>
            </ImageContainer>
          </MediaContent>
        </Content>
      </AnimatePresence>
      <ProgressBar>
        <ProgressIndicator
          key={currentIndex}
          initial={{ width: '0%' }}
          animate={{ width: '100%' }}
          transition={{ duration: SLIDE_DURATION, ease: 'linear' }}
        />
      </ProgressBar>
    </HeroSection>
  );
};

export default Hero;
